/**
 * The contact API endpoint. Baked at build time — the frame is a static bundle
 * served by a container that holds no env of its own (architecture doc §4).
 *
 * The local fallback port is NOT a free choice. A workspace owns a 10-port band
 * from its base (7600 here), and `infra/worktree/setup.sh` lets Postgres claim
 * the first free slot in base+5…base+9 — so +6…+9 are reserved fallbacks, and a
 * contact API parked there works until the day Postgres lands on it. That
 * failure would present as the widget losing its API, with nothing pointing at
 * a port choice made months earlier. +2 and +3 are the only unreserved slots:
 * the frame's dev server takes 7602, the contact API 7603.
 */
const CONTACT_API_URL = import.meta.env.VITE_SUPPORT_CONTACT_API_URL ?? 'http://localhost:7603/graphql';

interface GraphQlError {
  message: string;
  extensions?: {
    key?: string;
  };
}

interface GraphQlResponse<T> {
  data?: T | null;
  errors?: GraphQlError[];
}

/**
 * Everything this app throws. `key` is the API's `extensions.key` when the
 * failure came back as a GraphQL error, and `undefined` when it did not reach
 * that far — a network failure, or a step-1 origin rejection, which carries no
 * CORS header and so reaches the browser as an opaque failure with no key at
 * all (see the note on `ORIGIN_NOT_ALLOWED` in `@support/widget-contracts`).
 */
export class WidgetApiError extends Error {
  readonly key: string | undefined;

  constructor(message: string, key?: string) {
    super(message);
    this.name = 'WidgetApiError';
    this.key = key;
  }
}

export const request = async <T>(
  query: string,
  variables: Record<string, unknown>,
  sessionToken?: string,
): Promise<T> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  let response: Response;

  try {
    response = await fetch(CONTACT_API_URL, {
      method: 'POST',
      headers,
      // No cookies, in either direction. The frame is third-party on at least
      // one of the two host apps, where the browser drops them anyway — the
      // session is a bearer token held in memory instead (architecture doc §4).
      credentials: 'omit',
      body: JSON.stringify({ query, variables }),
    });
  } catch {
    throw new WidgetApiError('Could not reach support. Check your connection and try again.');
  }

  let payload: GraphQlResponse<T> | null = null;

  try {
    payload = await response.json() as GraphQlResponse<T>;
  } catch {
    payload = null;
  }

  const firstError = payload?.errors?.find(Boolean);

  if (firstError) {
    throw new WidgetApiError(firstError.message, firstError.extensions?.key);
  }

  if (!response.ok || !payload?.data) {
    throw new WidgetApiError('Support is unavailable right now. Please try again.');
  }

  return payload.data;
};
